import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  DollarSign,
  Car,
  GraduationCap,
  CreditCard,
  Home,
  Shield,
  Briefcase,
  ArrowRight,
  CheckCircle,
  Clock,
  Users,
  ChevronLeft,
  Sparkles,
} from "lucide-react";
import { ReactComponent as Guy } from "../../assets/svg/guy.svg";
import Select from "react-select";

const loanTypes = [
  {
    label: "Personal Loans",
    value: "any",
    icon: DollarSign,
    color: "blue",
    description: "Flexible funding for any purpose",
  },
  {
    label: "Student Loan Refinancing",
    value: "student_loan",
    icon: GraduationCap,
    color: "purple",
    description: "Lower your education debt payments",
  },
  {
    label: "Auto Refinancing",
    value: "auto",
    icon: Car,
    color: "green",
    description: "Reduce your car payment",
  },
  {
    label: "Debt Consolidation",
    value: "debt_consolidation",
    icon: CreditCard,
    color: "orange",
    description: "Combine multiple debts into one",
  },
  {
    label: "Home Improvement",
    value: "home_improvement",
    icon: Home,
    color: "teal",
    description: "Fund your renovation projects",
  },
  {
    label: "Business Loans",
    value: "business",
    icon: Briefcase,
    color: "indigo",
    description: "Grow your business",
  },
];

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    border: state.isFocused ? "2px solid #3b82f6" : "2px solid #4b5563",
    borderRadius: "16px",
    padding: "8px",
    backgroundColor: "rgba(31, 41, 55, 0.9)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
    "&:hover": {
      borderColor: "#3b82f6",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontWeight: "500",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
    backgroundColor: "#1f2937",
    border: "1px solid #374151",
    zIndex: 9999,
    overflow: "hidden",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#3b82f6"
      : state.isFocused
        ? "#374151"
        : "#1f2937",
    color: state.isSelected ? "white" : "#e5e7eb",
    cursor: "pointer",
    padding: "12px 16px",
    margin: "0",
    "&:active": {
      backgroundColor: "#3b82f6",
    },
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": {
      color: "#e5e7eb",
    },
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: "#4b5563",
  }),
};

const FinancingPage = () => {
  const [selected, setSelected] = useState("");
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    if (selected !== "") {
      // Check if script is already loaded
      const existingScript = document.querySelector(
        'script[src="https://www.moneylion.com/network/freede/loans/web-component/lending-explore/index.js"]'
      );

      if (existingScript) {
        setScriptLoaded(true);
        return;
      }

      // Create and load the script
      const script = document.createElement("script");
      script.src =
        "https://www.moneylion.com/network/freede/loans/web-component/lending-explore/index.js";
      script.type = "application/javascript";
      script.async = true;

      script.onload = () => {
        setScriptLoaded(true);
      };

      script.onerror = () => {
        console.error("Failed to load MoneyLion script");
      };

      document.head.appendChild(script);

      // Cleanup function
      return () => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
    } else {
      setScriptLoaded(false);
    }
  }, [selected]);

  if (selected !== "") {
    return (
      <div className="min-h-full p-6">
        <div className="max-w-7xl mx-auto">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: -5 }}
            onClick={() => setSelected("")}
            className="flex items-center gap-2 mb-6 px-4 py-2 bg-gray-800/50 hover:bg-gray-700/50 text-white rounded-xl transition-all"
          >
            <ChevronLeft size={20} />
            <span>Return to options</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-2xl overflow-hidden"
            style={{ height: "calc(100vh - 140px)" }}
          >
            {scriptLoaded ? (
              <moneylion-lending-explore></moneylion-lending-explore>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">Loading...</div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 overflow-x-hidden">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="absolute top-40 left-0 h-80 w-80 rounded-full bg-blue-600/20 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Finance made{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-500">
                friendly
              </span>
            </h1>
            <p className="text-xl text-gray-300">
              Find the perfect loan for your needs
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 relative z-50">
                <h2 className="text-2xl font-semibold text-white mb-4">
                  Quick Loan Finder
                </h2>
                <p className="text-gray-400 mb-6">
                  Select your loan type to get matched with the best offers
                </p>
                <Select
                  styles={customSelectStyles}
                  placeholder="Choose a loan type..."
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  options={loanTypes.map((type) => ({
                    ...type,
                    label: (
                      <div className="flex items-center gap-3">
                        <type.icon size={20} />
                        <div>
                          <div className="font-medium">{type.label}</div>
                          <div className="text-xs text-gray-500">
                            {type.description}
                          </div>
                        </div>
                      </div>
                    ),
                  }))}
                  onChange={(e) => setSelected(e.value)}
                />

                <div className="mt-6 flex items-center gap-4 text-sm text-gray-400">
                  <div className="flex items-center gap-2">
                    <Shield size={16} className="text-green-400" />
                    <span>Secure & Confidential</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-blue-400" />
                    <span>Quick Process</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="order-1 lg:order-2 flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-blue-600/20 rounded-full blur-2xl" />
                <Guy className="relative w-full max-w-md h-auto" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Loan Options Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Find the financial services{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-500">
              you need
            </span>
          </h2>
          <p className="text-gray-400">Click any option below to get started</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loanTypes.map((loan, index) => {
            const Icon = loan.icon;
            const colorClasses = {
              blue: "from-blue-500 to-blue-600",
              purple: "from-purple-500 to-purple-600",
              green: "from-green-500 to-green-600",
              orange: "from-orange-500 to-orange-600",
              teal: "from-teal-500 to-teal-600",
              indigo: "from-indigo-500 to-indigo-600",
            };

            return (
              <motion.div
                key={loan.value}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02, y: -5 }}
                onClick={() => setSelected(loan.value)}
                className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 hover:border-gray-600 cursor-pointer transition-all group"
              >
                <div
                  className={`w-14 h-14 rounded-xl bg-gradient-to-r ${colorClasses[loan.color]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                >
                  <Icon size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {loan.label}
                </h3>
                <p className="text-gray-400 text-sm mb-4">{loan.description}</p>
                <div className="flex items-center gap-2 text-blue-400 group-hover:gap-3 transition-all">
                  <span className="text-sm font-medium">Get Started</span>
                  <ArrowRight size={16} />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-gray-800/30 backdrop-blur-sm border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-gray-400">Match with personalized loan offers</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                number: "1",
                title: "Answer a few questions",
                description:
                  "Just fill out our simple form and we'll check offers from all the top providers for you.",
                icon: Clock,
              },
              {
                number: "2",
                title: "Get matched with top providers",
                description:
                  "Based on the info you provide and your creditworthiness, we match you with the right offer to meet your needs.",
                icon: Users,
              },
              {
                number: "3",
                title: "Close the deal",
                description:
                  "Choose the offer with the best terms for you and, if approved, get your funds or new account right away.",
                icon: CheckCircle,
              },
            ].map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 h-full">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xl">
                      {step.number}
                    </div>
                    <step.icon size={24} className="text-blue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm">{step.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight size={24} className="text-gray-600" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button
              onClick={() => setSelected("any")}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <Sparkles size={20} />
              Find Your Perfect Loan
              <ArrowRight size={20} />
            </button>

            <p className="mt-4 text-gray-500 text-sm">
              Freede from worry, Freede from stress, Freede from debt.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FinancingPage;
